import Vue from 'vue';
import { semEstoqueBloqueando } from '@/utils/estoque';

export const rotuloEtapa = passo => passo?.identificacao || passo?.descricao || '';

export const rotuloEtapaComCodigo = passo => {
    const rotulo = rotuloEtapa(passo);

    return passo?.id ? `${passo.id} - ${rotulo}` : rotulo;
};

export const limiteMaxEtapa = passo => +passo?.max || 20;

export const escolhaUnica = passo => limiteMaxEtapa(passo) === 1;

export const escolhidosNaEtapa = passo =>
    (passo?.complementos || []).reduce((soma, c) => soma + (+c.quantidade || 0), 0);

export const etapaCheia = passo => escolhidosNaEtapa(passo) >= limiteMaxEtapa(passo);

export const isIndisponivel = complemento =>
    complemento?.status === 'inativo' || semEstoqueBloqueando(complemento);

export const qtdMaxDoComplemento = (passo, complemento) =>
    +complemento?.max || limiteMaxEtapa(passo);

export const podeSomar = (passo, complemento) => {
    const atual = +complemento.quantidade || 0;

    if (atual >= qtdMaxDoComplemento(passo, complemento)) return false;

    return !etapaCheia(passo);
};

export const podeTocar = (passo, complemento) => {
    if (escolhaUnica(passo)) return true;

    return podeSomar(passo, complemento);
};

export const somar = (passo, complemento) => {
    if (!podeSomar(passo, complemento)) return false;

    Vue.set(complemento, 'quantidade', (+complemento.quantidade || 0) + 1);
    return true;
};

export const tocar = (passo, complemento) => {
    if (!podeTocar(passo, complemento)) return false;

    if (escolhaUnica(passo)) {
        (passo.complementos || []).forEach(c => Vue.set(c, 'quantidade', c === complemento ? 1 : 0));
        return true;
    }

    return somar(passo, complemento);
};

export const remover = complemento => {
    const atual = +complemento.quantidade || 0;
    if (atual <= 0) return false;

    Vue.set(complemento, 'quantidade', atual - 1);
    return true;
};

export const definirQuantidade = (passo, complemento, valor) => {
    const atual = +complemento.quantidade || 0;
    let nova = Math.max(0, Math.floor(+valor || 0));

    if (nova === atual) {
        return { quantidade: atual, aviso: null };
    }

    const sobraDaEtapa = limiteMaxEtapa(passo) - (escolhidosNaEtapa(passo) - atual);
    const teto = Math.max(0, Math.min(qtdMaxDoComplemento(passo, complemento), sobraDaEtapa));

    let aviso = null;

    if (nova > teto) {
        nova = teto;
        aviso = `Quantidade máxima permitida: ${teto}`;
    }

    Vue.set(complemento, 'quantidade', nova);

    return { quantidade: nova, aviso };
};

export const pendenciaDaEtapa = passo => {
    const minimo = +passo?.min || 0;
    const escolhidos = escolhidosNaEtapa(passo);

    if (minimo && escolhidos < minimo) {
        return minimo === 1 ? 'Escolha uma opção' : `Escolha ao menos ${minimo}`;
    }

    const abaixoDoMinimo = (passo?.complementos || [])
        .find(c => +c.quantidade > 0 && +c.quantidade < +c.min);

    if (abaixoDoMinimo) {
        return `Mínimo ${abaixoDoMinimo.min} de ${abaixoDoMinimo.descricao}`;
    }

    return null;
};

export const instrucaoDaEtapa = passo => {
    const min = +passo?.min || 0;
    const max = limiteMaxEtapa(passo);
    const n = escolhidosNaEtapa(passo);
    const escolhidos = n ? ` · ${n} escolhido${n === 1 ? '' : 's'}` : '';

    if (max === 1) return min ? 'Escolha 1 opção' : 'Escolha 1, se quiser';
    if (min && min === max) return `Escolha ${min}${escolhidos}`;
    if (min) return `Escolha de ${min} a ${max}${escolhidos}`;

    return `Até ${max} opções${escolhidos}`;
};

export const seloDaEtapa = passo => {
    if (!+passo?.min) return { texto: 'Opcional', classe: 'selo--opcional' };

    return pendenciaDaEtapa(passo)
        ? { texto: 'Obrigatório', classe: 'selo--obrigatorio' }
        : { texto: 'Pronto', classe: 'selo--ok' };
};

export const etapasDoProduto = produto => {
    if (!produto) return [];

    if (produto.produtos?.length) {
        const lista = [];

        produto.produtos.forEach(sub => {
            (sub.passos || []).forEach(passo => lista.push({ passo, contexto: sub.descricao }));
        });

        return lista;
    }

    return (produto.passos || []).map(passo => ({ passo, contexto: null }));
};

export const limparEscolhasVazias = produto => {
    const limpar = alvo => {
        if (!alvo?.passos?.length) return;

        alvo.passos.forEach(passo => {
            passo.complementos = (passo.complementos || []).filter(c => +c.quantidade > 0);
        });

        alvo.passos = alvo.passos.filter(passo => passo.complementos.length);
    };

    if (produto?.produtos?.length) {
        produto.produtos.forEach(limpar);
        return produto;
    }

    limpar(produto);
    return produto;
};

export const zerarEscolhas = produto => {
    const zerar = passos => (passos || []).forEach(passo => {
        (passo.complementos || []).forEach(c => Vue.set(c, 'quantidade', 0));
    });

    zerar(produto?.passos);
    (produto?.produtos || []).forEach(sub => zerar(sub.passos));

    return produto;
};
